import { computed } from 'vue';
import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from './global-store-base';
import { useBrand } from './brand';
import { TariffCodeSystemType, TariffCodeField, type TariffCodeDto, type TariffCodeSystemDto } from '@/ApiClient';
import { assertUnreachable } from '@lib/type-helpers.ts';
import { useUser } from '@/store/user';

export const useTariffCodeSystems = defineStore('tariff-code-systems', () => {
  const api = useApi();

  const globalStoreBase = useGlobalStore({
    load: api.tariffCodeSystem_GetTariffCodeSystems,
    dto: 'TariffCodeSystemDto',
    create: api.tariffCodeSystem_Create,
    update: api.tariffCodeSystem_Update,
    delete: api.tariffCodeSystem_Delete,
    signalR: 'TariffCodeSystemUpdate',
  });

  // Computed properties to match old store interface
  const systems = computed(() => {
    const result: Record<number, { region: string; name: string }> = {};
    globalStoreBase.loaded.value.forEach((system) => {
      result[system.type] = {
        region: system.name,
        name: system.name,
      };
    });
    return result;
  });

  const systemKeys = computed(() => {
    return globalStoreBase.loaded.value.map((system) => system.type);
  });

  const getMergedSystemName = (key: number) => {
    const system = systems.value[key];
    return system ? system.region : '';
  };

  // Flat list of ALL codes across all systems with metadata
  const allCodes = computed(() => {
    return globalStoreBase.loaded.value.flatMap((system) =>
      system.tariffCodes.map((code) => ({
        ...code,
        systemId: system.id,
        systemType: system.type,
        systemName: system.name,
      }))
    );
  });

  // Look up code by ID (returns code with system metadata)
  const getCodeById = (id: number) => {
    return allCodes.value.find((c) => c.id === id)!;
  };

  // Get multiple codes by IDs
  const getCodesByIds = (ids: number[]) => {
    return ids.map((id) => getCodeById(id));
  };

  // Search codes within a specific system (searches value, name, AND description)
  const searchCodesInSystem = (systemType: number, query: string) => {
    const system = globalStoreBase.loaded.value.find((s) => s.type === systemType);
    if (!system) return [];

    const lowerQuery = query.toLowerCase();
    return system.tariffCodes.filter(
      (c) =>
        c.code.toLowerCase().includes(lowerQuery) ||
        c.name?.toLowerCase().includes(lowerQuery) ||
        c.description?.toLowerCase().includes(lowerQuery)
    );
  };

  // Search codes across all systems (searches code, name, AND description)
  const searchCodes = (systemId: number, query: string) => {
    const system = globalStoreBase.loaded.value.find((s) => s.id === systemId);
    if (!system) return [];

    if (!query || query.trim().length === 0) return system.tariffCodes;

    const lowerQuery = query.toLowerCase();
    return system.tariffCodes.filter(
      (c) =>
        c.code.toLowerCase().includes(lowerQuery) ||
        c.name?.toLowerCase().includes(lowerQuery) ||
        c.description?.toLowerCase().includes(lowerQuery)
    );
  };

  // Get system by type
  const getSystemByType = (systemType: number) => {
    return globalStoreBase.loaded.value.find((s) => s.type === systemType);
  };

  // Get system type label (short code like EU, US, etc)
  const getSystemTypeLabel = (systemType: number): string => {
    const labels: Record<number, string> = {
      [TariffCodeSystemType.EU]: 'EU',
      [TariffCodeSystemType.US]: 'US',
      [TariffCodeSystemType.EUCN]: 'EU-CN',
      [TariffCodeSystemType.FEDAS]: 'FEDAS',
      [TariffCodeSystemType.NO]: 'NO',
      [TariffCodeSystemType.RU]: 'RU',
      [TariffCodeSystemType.AVALARA]: 'Avalara',
      [TariffCodeSystemType.CA]: 'CA',
      [TariffCodeSystemType.DE]: 'DE',
    };
    return labels[systemType] || `Type ${systemType}`;
  };

  // Available system types with full labels for selection (e.g., in admin UI)
  const availableSystemTypes = [
    { value: TariffCodeSystemType.EU, label: 'EU (Taric)' },
    { value: TariffCodeSystemType.US, label: 'US (HTS)' },
    { value: TariffCodeSystemType.EUCN, label: 'EU-CN (Combined Nomenclature)' },
    { value: TariffCodeSystemType.FEDAS, label: 'FEDAS (PCK)' },
    { value: TariffCodeSystemType.NO, label: 'Norway' },
    { value: TariffCodeSystemType.RU, label: 'Russia' },
    { value: TariffCodeSystemType.AVALARA, label: 'Avalara' },
    { value: TariffCodeSystemType.CA, label: 'Canada' },
    { value: TariffCodeSystemType.DE, label: 'Germany' },
  ];

  // Add tariff code to system
  const addCode = async (systemId: number, value: string): Promise<TariffCodeDto> => {
    const updatedSystem = await api.tariffCode_AddTariffCode(useUser().brandId, {
      tariffCodeSystemId: systemId,
      value,
    });

    // Find and return the newly created code
    const newCode = updatedSystem.tariffCodes.find((c) => c.code === value);
    if (!newCode) throw new Error('Failed to create tariff code');

    return newCode;
  };

  // Copy tariff codes (creates duplicates with "-copy" suffix)
  const copyTariffCodes = async (systemId: number, tariffCodeIds: number[]): Promise<void> => {
    await api.tariffCode_CopyTariffCodes(useUser().brandId, {
      tariffCodeSystemId: systemId,
      tariffCodeIds,
    });
  };

  // Update tariff code value, name, and description
  const updateCode = async (
    systemId: number,
    tariffCodeId: number,
    value: string,
    name?: string | null,
    description?: string | null
  ): Promise<void> => {
    await api.tariffCode_UpdateTariffCode(useUser().brandId, tariffCodeId, {
      value,
      name: name ?? undefined,
      description: description ?? undefined,
    });
  };

  /**
   * Fold the system a write answered with back into the list. Without it the table keeps
   * rendering the old codes and duties until the write's SignalR event arrives and the
   * reload returns, which reads as a flicker: the value reverts for that round trip.
   */
  const applySystem = (system: TariffCodeSystemDto) => {
    const index = globalStoreBase.loaded.value.findIndex((s) => s.id === system.id);
    if (index > -1) globalStoreBase.loaded.value[index] = system;
  };

  // Batch update a single field for multiple tariff codes
  const batchUpdateCodes = async (
    systemId: number,
    tariffCodeIds: number[],
    field: TariffCodeField,
    value: string | null
  ): Promise<void> => {
    applySystem(
      await api.tariffCode_BatchUpdateTariffCodes(useUser().brandId, {
        tariffCodeSystemId: systemId,
        ids: tariffCodeIds,
        field,
        value: value ?? undefined,
      })
    );
  };

  // Write one value per cell, which is what a pasted block is
  const batchSetCodeFields = async (
    systemId: number,
    entries: { tariffCodeId: number; field: TariffCodeField; value: string | null }[]
  ): Promise<void> => {
    applySystem(
      await api.tariffCode_BatchSetTariffCodeFields(useUser().brandId, {
        tariffCodeSystemId: systemId,
        entries: entries.map(({ tariffCodeId, field, value }) => ({
          id: tariffCodeId,
          field,
          value: value ?? undefined,
        })),
      })
    );
  };

  // Remove tariff code from system
  const removeCode = async (systemId: number, tariffCodeId: number): Promise<void> => {
    await api.tariffCode_DeleteTariffCode(useUser().brandId, tariffCodeId);
  };

  // Validation helpers
  const cleanTariffCode = (value: string): string => {
    return value.replace(/ /g, '');
  };

  const validateTariffCodeFormat = (value: string, systemType: TariffCodeSystemType): boolean => {
    if (!value || value.trim().length === 0) return false;

    // Clean the value (remove spaces) - matches backend CleanTariffCode
    const cleanValue = cleanTariffCode(value);

    // System-specific validation (matches backend regex patterns exactly)
    switch (systemType) {
      case TariffCodeSystemType.EU: // EU/Taric
        // Backend: ^[ ]*[0-9]{10}[ ]*$ (after cleaning: must be exactly 10 digits)
        return /^[0-9]{10}$/.test(cleanValue);

      case TariffCodeSystemType.EUCN: // EUCN
        // Backend: ^[ ]*[0-9]{8}[ ]*$ (after cleaning: must be exactly 8 digits)
        return /^[0-9]{8}$/.test(cleanValue);

      case TariffCodeSystemType.US: // US/HTS
        // Backend: ^[ ]*[0-9]{10}[ ]*$ (after cleaning: must be exactly 10 digits)
        return /^[0-9]{10}$/.test(cleanValue);

      case TariffCodeSystemType.FEDAS: // FEDAS
        // Backend: ^[ ]*[0-9]{6}[ ]*$ (after cleaning: must be exactly 6 digits)
        return /^[0-9]{6}$/.test(cleanValue);

      case TariffCodeSystemType.NO: // Norway
        // Backend: ^[ ]*[0-9]{8}[ ]*$ (after cleaning: must be exactly 8 digits)
        return /^[0-9]{8}$/.test(cleanValue);

      case TariffCodeSystemType.RU: // Russia
        // Backend: ^[ ]*[0-9]{10}[ ]*$ (after cleaning: must be exactly 10 digits)
        return /^[0-9]{10}$/.test(cleanValue);

      case TariffCodeSystemType.AVALARA: // Avalara
        // Backend: ^[ ]*[a-zA-Z][a-zA-Z0-9]([0-9]{6})?[ ]*$
        // Must be 1 letter + 1 alphanumeric, optionally followed by 6 digits (2 or 8 chars total)
        return /^[a-zA-Z][a-zA-Z0-9]([0-9]{6})?$/.test(cleanValue);

      case TariffCodeSystemType.CA: // Canada
        // Backend: ^[ ]*[0-9]{10}[ ]*$ (after cleaning: must be exactly 10 digits)
        return /^[0-9]{10}$/.test(cleanValue);

      case TariffCodeSystemType.DE: // Germany
        // Backend: ^[ ]*[0-9]{11}[ ]*$ (after cleaning: must be exactly 11 digits)
        return /^[0-9]{11}$/.test(cleanValue);

      default:
        return assertUnreachable(systemType);
    }
  };

  // Check if a code is valid for a given system (accounts for skipValidation)
  const isCodeValidForSystem = (code: string, systemId: number): boolean => {
    const system = globalStoreBase.loaded.value.find((s) => s.id === systemId);
    if (!system) return false;

    // If validation is skipped, all codes are valid
    if (system.skipValidation) return true;

    // Otherwise check format
    return validateTariffCodeFormat(code, system.type);
  };

  // Duty management methods
  const addDuty = async (
    codeId: number,
    seasonId: number | null | undefined,
    countryId: number,
    dutyPercentage: number
  ): Promise<void> => {
    await api.tariffCode_AddDuty(useUser().brandId, codeId, {
      seasonId: seasonId ?? undefined,
      countryId,
      dutyPercentage,
    });
  };

  const updateDuty = async (codeId: number, dutyId: number, dutyPercentage: number): Promise<void> => {
    await api.tariffCode_UpdateDuty(useUser().brandId, codeId, dutyId, {
      dutyPercentage,
    });
  };

  const removeDuty = async (codeId: number, dutyId: number): Promise<void> => {
    await api.tariffCode_DeleteDuty(useUser().brandId, codeId, dutyId);
  };

  /** Each cell carries its own value, for a clipboard paste. null clears the duty. */
  const batchSetDuties = async (
    systemId: number,
    seasonId: number | null | undefined,
    entries: { tariffCodeId: number; countryId: number; dutyPercentage: number | null }[]
  ): Promise<void> => {
    applySystem(
      await api.tariffCode_BatchUpdateDuties(useUser().brandId, {
        tariffCodeSystemId: systemId,
        seasonId: seasonId ?? undefined,
        dutyPercentage: undefined,
        entries: entries.map((entry) => ({
          tariffCodeId: entry.tariffCodeId,
          countryId: entry.countryId,
          hasValue: true,
          dutyPercentage: entry.dutyPercentage ?? undefined,
        })),
      })
    );
  };

  const reorderSystems = async (systemIds: number[]): Promise<void> => {
    await api.tariffCodeSystem_UpdateOrder(useUser().brandId, systemIds);
  };

  return {
    ...globalStoreBase,
    systems,
    systemKeys,
    getMergedSystemName,
    allCodes,
    getCodeById,
    getCodesByIds,
    searchCodes,
    searchCodesInSystem,
    getSystemByType,
    getSystemTypeLabel,
    availableSystemTypes,
    addCode,
    copyTariffCodes,
    updateCode,
    batchUpdateCodes,
    batchSetCodeFields,
    removeCode,
    isCodeValidForSystem,
    validateTariffCodeFormat,
    addDuty,
    updateDuty,
    removeDuty,
    batchSetDuties,
    reorderSystems,
  };
});
